import React, { ChangeEvent, useEffect, useState } from 'react';
import './ZipcodePanel.scss';
import AhmIcon from 'components/Global/AhmIcon/AhmIcon';
import { getZipUrl, setZipcode, getZipcode } from 'helpers/Zipgate';
import { checkURL, trackEvent } from 'helpers/Analytics';

interface IZipcodePanel {
  stationsAmount: number;
  placeholder: string;
  disable: boolean;
}

const ZipcodePanel = ({
  stationsAmount,
  placeholder,
  disable,
}: IZipcodePanel): JSX.Element => {
  var currentZipcode = getZipcode();
  const [zipcodeInput, setZipcodeInput] = useState(currentZipcode);
  const [zipcodeError, setZipcodeError] = useState(false);
  const isHonda = process.env.REACT_APP_THEME === 'honda';
  const trackingObject = isHonda
    ? { Page: { page_name: '' } }
    : window.Acr.trackingObject || {};
  const dspToken = sessionStorage.getItem('dsp_token');

  const validateZip = async () => {
    const response: any = await getZipUrl(zipcodeInput);
    if (response && response.data && response.data[zipcodeInput]) {
      const zipData = response.data[zipcodeInput];
      const result =
        zipData.address &&
        zipData.address.countryRegion === 'United States' &&
        zipData.confidence === 'High';
      if (result) {
        if (zipcodeError) {
          setZipcodeError(false);
        }
        setZipcode(zipcodeInput);
        if (!isHonda) {
          trackEvent({
            label: 'csl:search submit',
            category: {
              includePageName: false,
              text: 'csl:search',
            },
            type: 'selector',
            modelSeries: 'zdx',
            modelYear: '2024',
            bodyStyle: 'suvs',
            environment: checkURL(trackingObject.Page?.full_url || ''),
            locator: {
              Page: {
                ...trackingObject.Page,
                page_friendly_url: trackingObject.Page?.full_url || '',
              },
              User: {
                user_zipcode: getZipcode(),
                user_status: dspToken ? 'logged_in' : 'not_logged_in',
              },
              GlobalMetadata: {
                property_name: 'acura.com',
                version_dl: 'V1.0',
              },
            },
          });
        } else {
          trackEvent({
            label: 'csl:search submit',
            category: {
              includePageName: false,
              text: 'csl:search',
            },
            type: 'selector',
            modelSeries: 'zdx',
            modelYear: '2024',
            bodyStyle: 'suvs',
          });
        }
      } else {
        setZipcodeError(true);
        if (!isHonda) {
          trackEvent({
            label: 'csl:search submit:error',
            category: {
              includePageName: false,
              text: 'csl:search',
            },
            type: 'selector',
            modelSeries: 'zdx',
            modelYear: '2024',
            bodyStyle: 'suvs',
            environment: checkURL(trackingObject.Page?.full_url || ''),
            locator: {
              Page: {
                ...trackingObject.Page,
                page_friendly_url: trackingObject.Page?.full_url || '',
              },
              User: {
                user_zipcode: getZipcode(),
                user_status: dspToken ? 'logged_in' : 'not_logged_in',
              },
              GlobalMetadata: {
                property_name: 'acura.com',
                version_dl: 'V1.0',
              },
            },
          });
        } else {
          trackEvent({
            label: 'csl:search submit:error',
            category: {
              includePageName: false,
              text: 'csl:search',
            },
            type: 'selector',
            modelSeries: 'zdx',
            modelYear: '2024',
            bodyStyle: 'suvs',
          });
        }
      }
    } else {
      setZipcodeError(true);
      if (!isHonda) {
        trackEvent({
          label: 'csl:search submit:error',
          category: {
            includePageName: false,
            text: 'csl:search',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
          environment: checkURL(trackingObject.Page?.full_url || ''),
          locator: {
            Page: {
              ...trackingObject.Page,
              page_friendly_url: trackingObject.Page?.full_url || '',
            },
            User: {
              user_zipcode: getZipcode(),
              user_status: dspToken ? 'logged_in' : 'not_logged_in',
            },
            GlobalMetadata: {
              property_name: 'acura.com',
              version_dl: 'V1.0',
            },
          },
        });
      } else {
        trackEvent({
          label: 'csl:search submit:error',
          category: {
            includePageName: false,
            text: 'csl:search',
          },
          type: 'selector',
          modelSeries: 'zdx',
          modelYear: '2024',
          bodyStyle: 'suvs',
        });
      }
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (zipcodeError) {
      setZipcodeError(false);
    }
    const { value } = e.target;
    setZipcodeInput(value);
  };

  const clearInput = () => {
    if (zipcodeError) {
      setZipcodeError(false);
    }
    setZipcodeInput('');
  };

  const typeVariation = isHonda
    ? 'charging-zipcode-panel-honda'
    : 'charging-zipcode-panel';

  const errorMessage = isHonda
    ? 'charging-zipcode-panel-honda__message-error'
    : 'charging-zipcode-panel__message';

  useEffect(() => {
    setZipcodeInput(currentZipcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentZipcode]);

  return (
    <div className={'charging-zipcode-panel'}>
      <div className={'charging-zipcode-panel__input-container'}>
        <input
          name={'Charging locator zipcode input'}
          className={`${typeVariation}__input ahm-legacy-label2 ${
            zipcodeError ? 'error' : ''
          }`}
          value={zipcodeInput}
          disabled={disable}
          maxLength={5}
          placeholder={placeholder}
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              zipcodeInput !== '' && validateZip();
            }
          }}
          onChange={handleInputChange}
        />
        {zipcodeInput !== '' && (
          <button
            className={`${typeVariation}__close-icon`}
            onClick={clearInput}
          >
            <AhmIcon iconName="close" />
          </button>
        )}
        {zipcodeError ? (
          <div className={`${typeVariation}__error-icon`}>
            <AhmIcon iconName="warning" />
          </div>
        ) : (
          <button
            className={`${typeVariation}__search-icon`}
            onClick={() => zipcodeInput !== '' && validateZip()}
          >
            {isHonda ? (
              <AhmIcon iconName="search" />
            ) : (
              <AhmIcon iconName="searchAlternative" />
            )}
          </button>
        )}
      </div>
      {(stationsAmount >= 0 || zipcodeError) && (
        <>
          <div
            className={`charging-zipcode-panel__message-container ${
              zipcodeError ? 'visible' : ''
            }`}
          >
            <p className={`${errorMessage} ahm-legacy-card-cta`}>
              {`We can't find "${zipcodeInput}"`}
            </p>
            <p className={`${typeVariation}__message ahm-legacy-label4`}>
              Please make sure your zipcode is formatted correctly.
            </p>
          </div>
          <div
            className={`charging-zipcode-panel__message-container ${
              !zipcodeError ? 'visible' : ''
            }`}
          >
            <p className={`${typeVariation}__message ahm-legacy-label4`}>
              {`${stationsAmount} stations in this area. Please click “Search This Area” to refresh your results.`}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ZipcodePanel;
